import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Bell, Check, CheckCheck, Smartphone, Wifi, WifiOff, User, AlertCircle, X } from 'lucide-react';

/**
 * BotNotifications - Component to display bot connection notifications
 * Shows when users connect via WhatsApp/Telegram bot
 */
export default function BotNotifications({ className = '' }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('bot_notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        // Table might not exist yet
        if (error.code === '42P01') {
          console.log('[BotNotifications] Table does not exist yet');
          return;
        }
        throw error;
      }

      setNotifications(data || []);
      setUnreadCount(data?.filter(n => !n.read).length || 0);
    } catch (err) {
      console.warn('[BotNotifications] Error fetching:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    
    // Subscribe to realtime updates
    const channel = supabase
      .channel('bot_notifications_changes')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'bot_notifications',
      }, (payload) => {
        setNotifications(prev => [payload.new, ...prev].slice(0, 20));
        setUnreadCount(prev => prev + 1);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNotifications]);

  const markAsRead = async (ids) => {
    if (!ids.length) return;
    
    try {
      await supabase.rpc('mark_bot_notifications_read', { notification_ids: ids });
      setNotifications(prev => 
        prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - ids.length));
    } catch (err) {
      console.warn('[BotNotifications] Error marking as read:', err.message);
    }
  };

  const markAllAsRead = () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    markAsRead(unreadIds);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'connection_test':
        return <Wifi className="w-4 h-4 text-green-500" />;
      case 'login':
        return <User className="w-4 h-4 text-blue-500" />;
      case 'logout':
        return <User className="w-4 h-4 text-gray-500" />;
      case 'bot_action':
        return <Smartphone className="w-4 h-4 text-purple-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatNotification = (notification) => {
    const data = notification.data || {};
    const time = new Date(notification.created_at).toLocaleString();
    
    switch (notification.notification_type) {
      case 'connection_test':
        return {
          title: data.success ? 'Bot conectado' : 'Error de conexión',
          description: `${data.user_name || data.user_email || 'Usuario'} probó conexión desde ${data.source || 'bot'}`,
          detail: data.latency ? `Latencia: ${data.latency}ms` : null,
          time,
        };
      case 'login':
        return {
          title: 'Inicio de sesión',
          description: `${data.user_name || data.user_email} inició sesión desde ${data.source || 'bot'}`,
          time,
        };
      case 'logout':
        return {
          title: 'Sesión cerrada',
          description: `${data.user_name || data.user_email} cerró sesión`,
          time,
        };
      case 'bot_action':
        return {
          title: formatActionTitle(data.action),
          description: `${data.user_name || data.user_email || 'Usuario'} - ${formatActionDetail(data)}`,
          time,
        };
      default:
        return {
          title: notification.notification_type,
          description: JSON.stringify(data).substring(0, 100),
          time,
        };
    }
  };

  const formatActionTitle = (action) => {
    const titles = {
      'create_sale': '💳 Nueva venta',
      'create_order': '📦 Nuevo pedido',
      'update_status': '🔄 Estado actualizado',
      'mark_delivered': '✅ Entrega registrada',
    };
    return titles[action] || action;
  };

  const formatActionDetail = (data) => {
    if (data.transaction_number) {
      return `Transacción ${data.transaction_number}${data.amount ? ` - $${data.amount}` : ''}`;
    }
    return data.action || '';
  };

  // If no notifications and table doesn't exist, don't show anything
  if (notifications.length === 0 && !isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${className}`}
        title="Notificaciones del Bot"
      >
        <Smartphone className="w-5 h-5 text-gray-500" />
      </button>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        title="Notificaciones del Bot"
      >
        <Smartphone className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-blue-500" />
                <span className="font-medium text-gray-900 dark:text-white">Actividad del Bot</span>
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-blue-500 hover:text-blue-600 flex items-center gap-1"
                  >
                    <CheckCheck className="w-3 h-3" />
                    Marcar leídas
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Smartphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Sin actividad del bot</p>
                  <p className="text-xs mt-1">Las conexiones aparecerán aquí</p>
                </div>
              ) : (
                notifications.map((notification) => {
                  const formatted = formatNotification(notification);
                  return (
                    <div
                      key={notification.id}
                      onClick={() => !notification.read && markAsRead([notification.id])}
                      className={`px-4 py-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors ${
                        !notification.read ? 'bg-blue-50/50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {getNotificationIcon(notification.notification_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                              {formatted.title}
                            </p>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {formatted.description}
                          </p>
                          {formatted.detail && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                              {formatted.detail}
                            </p>
                          )}
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {formatted.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
